import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { settingsChangesQueryKey } from '@/api/useCompanySettings';
import { currentUserQueryKey } from '@/auth/useCurrentUser';

export type UserSummary = components['schemas']['UserSummaryResponse'];
export type UserRole = NonNullable<components['schemas']['UserRole']>;

/** Every role, in the order the pickers list them: most access first. */
export const USER_ROLES: readonly UserRole[] = ['Owner', 'Admin', 'Dispatcher', 'Technician'];

/**
 * The roster's cache key — the same one People reads (`routes/People.tsx`),
 * and the parent of every `['users', …]` entry: the board's and the pickers'
 * technicians (`['users', 'technicians']`) and each profile (`['users', id]`).
 * Invalidating it reaches all of them.
 */
export const usersQueryKey = ['users'] as const;

/**
 * The API said no, in words that can be shown: a 409's sentence (the JSON
 * string body — `UserEndpoints`' last-Owner, not-invited, revoke-instead,
 * yourself, reactivate and seat-cap refusals), or a 400's errors by field.
 * A 403 has no body (the Owner rule); `forbidden` lets the screen say it.
 */
export class UserRefusedError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors: Record<string, string[]> = {},
  ) {
    super(message);
    this.name = 'UserRefusedError';
  }

  get forbidden() {
    return this.status === 403;
  }
}

function refusal(status: number, error: unknown, what: string): Error {
  if (status === 409 && typeof error === 'string') return new UserRefusedError(error, status);
  if (status === 403) return new UserRefusedError('', status);
  const errors = (error as { errors?: Record<string, string[]> } | undefined)?.errors;
  if (status === 400 && errors) {
    return new UserRefusedError(Object.values(errors).flat()[0] ?? '', status, errors);
  }
  return new Error(`${what} failed: ${status}`);
}

/**
 * Everyone in the account, with each invitation's state (M2-20). Explicit
 * staleTime, so a test at the helper's 0 cannot hide a missing invalidation.
 */
export function useUserRoster() {
  return useQuery({
    queryKey: usersQueryKey,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/users');
      if (!response.ok || !data) throw new Error(`GET /api/v1/users failed: ${response.status}`);
      return data;
    },
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * After any change: the roster and everything under it (the board's columns,
 * the pickers, profiles), and the settings trail, which records every change.
 */
function useAfterUserChange() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: usersQueryKey });
    void queryClient.invalidateQueries({ queryKey: settingsChangesQueryKey });
  };
}

export interface InviteUserBody {
  name: string;
  email: string;
  role: UserRole;
}

export function useInviteUser() {
  const after = useAfterUserChange();
  return useMutation({
    mutationFn: async ({ name, email, role }: InviteUserBody) => {
      const { data, error, response } = await apiClient.POST('/api/v1/users', {
        body: { name, email, phone: null, role },
      });
      if (!response.ok || !data) throw refusal(response.status, error, 'Inviting');
      return data;
    },
    onSuccess: after,
  });
}

/** A new link; the old one stops working. */
export function useResendInvite() {
  const after = useAfterUserChange();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await apiClient.POST('/api/v1/users/{id}/invite', {
        params: { path: { id } },
      });
      if (!response.ok || !data) throw refusal(response.status, error, 'Resending');
      return data;
    },
    onSuccess: after,
  });
}

export function useRevokeInvite() {
  const after = useAfterUserChange();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await apiClient.DELETE('/api/v1/users/{id}/invite', {
        params: { path: { id } },
      });
      if (!response.ok) throw refusal(response.status, error, 'Revoking');
    },
    onSuccess: after,
  });
}

export function useChangeUserRole(callerId: string | undefined) {
  const after = useAfterUserChange();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: UserRole }) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/users/{id}/role', {
        params: { path: { id } },
        body: { role },
      });
      if (!response.ok || !data) throw refusal(response.status, error, 'Changing the role');
      return data;
    },
    onSuccess: (user) => {
      after();
      // Your own role: what you may see and do changes with it, at once —
      // not after /me's five-minute staleTime.
      if (user.id === callerId) void queryClient.invalidateQueries({ queryKey: currentUserQueryKey });
    },
  });
}

/**
 * "N upcoming visits will be unassigned", for the confirmation. Read afresh
 * every time the dialog opens (`staleTime: 0` refetches on mount); the dialog
 * counts a refetch as still counting, so a number read a minute ago is never
 * what someone confirms against.
 */
export function useDeactivationPreview(id: string | null) {
  return useQuery({
    enabled: id !== null,
    queryKey: ['users', id ?? '', 'deactivation'] as const,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/users/{id}/deactivation', {
        params: { path: { id: id! } },
      });
      if (!response.ok || !data) throw new Error(`GET deactivation failed: ${response.status}`);
      return Number(data.upcomingVisits);
    },
    staleTime: 0,
    retry: false,
  });
}

export function useDeactivateUser() {
  const after = useAfterUserChange();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await apiClient.POST('/api/v1/users/{id}/deactivate', {
        params: { path: { id } },
      });
      if (!response.ok || !data) throw refusal(response.status, error, 'Deactivating');
      return { user: data.user, unassigned: Number(data.unassignedVisits) };
    },
    onSuccess: () => {
      after();
      // Their upcoming visits were unassigned in the same transaction: the
      // board, the job list, the dashboard (all under ['jobs']) and the
      // board's per-technician days are drawn from those rows.
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useReactivateUser() {
  const after = useAfterUserChange();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await apiClient.POST('/api/v1/users/{id}/reactivate', {
        params: { path: { id } },
      });
      if (!response.ok || !data) throw refusal(response.status, error, 'Reactivating');
      return data;
    },
    onSuccess: after,
  });
}
